// Bulk-intake draft_data uses the raw extraction schema (full_name, title,
// start_date, work_experience...) — same schema the finalize step
// (_finalize_one_item) reads directly. mapDraftToFormValues/mapFormValuesToDraft
// are the form-friendly view of it and back.
//
// Kept in their own module (rather than inline in BulkExtractionReivew.jsx)
// so they're unit-testable without pulling in that page's heavy transitive
// imports (ResumePreview -> react-pdf, etc.).

import { mergeRequiredLanguageRows, toLanguageStrings } from "@/utils/languageRequirements";
import { websiteValue } from "@/utils/website";

/**
 * `alternate_email`/`previous_alternate_email` come from `item`, not
 * `draft` — the server returns them as siblings of `draft_data` on the
 * batch-detail item.
 *
 * `jobRequiredLanguages` is the job's own rows (`languages_required` off
 * `JobService.fetchJobById`) — prefilled first and locked in the editor
 * (`mergeRequiredLanguageRows`), same as the candidate-facing apply flow, so a
 * recruiter reviewing a bulk-uploaded resume answers the job's own language
 * questions instead of finding them blank.
 */
export const mapDraftToFormValues = (draft, item = {}, jobRequiredLanguages = []) => {
  const groupedSections = {};
  (draft?.additional_sections || []).forEach((sec) => {
    const title = sec?.section_title || "Other";
    if (!groupedSections[title]) groupedSections[title] = [];
    groupedSections[title].push(sec?.fields || {});
  });

  return {
    personal_information: {
      name: draft?.personal_information?.full_name || "",
      email: draft?.personal_information?.email || "",
      alternate_email: item?.alternate_email ?? item?.previous_alternate_email ?? "",
      phone: draft?.personal_information?.phone || "",
      location: draft?.personal_information?.location || "",
      linkedin: draft?.personal_information?.linkedin || "",
      portfolio: draft?.personal_information?.website || "",
      summary: draft?.summary || "",
    },
    // Rows for the paired dropdowns — job-required rows first and locked,
    // then whatever the resume itself reported, same as the apply flow.
    skills: { list: draft?.skills || [], languages: mergeRequiredLanguageRows(jobRequiredLanguages, draft?.languages) },
    experience: (draft?.work_experience || []).map((e) => ({
      company: e.company || "",
      jobTitle: e.title || "",
      startDate: e.start_date || "",
      endDate: e.end_date || "",
      location: e.location || "",
      description: e.description || "",
      achievements: e.achievements || [],
    })),
    education: (draft?.education || []).map((e) => ({
      institution: e.institution || "",
      degree: e.degree || "",
      fieldOfStudy: e.field_of_study || "",
      startDate: e.start_date || "",
      endDate: e.end_date || "",
      grade: e.grade || "",
    })),
    compensation: {
      current_salary: draft?.current_salary != null ? String(draft.current_salary) : "",
      expected_salary: draft?.expected_salary != null ? String(draft.expected_salary) : "",
      notice_period: draft?.notice_period || "",
      total_experience: draft?.total_experience_years != null ? String(draft.total_experience_years) : "",
    },
    additionalSections: Object.entries(groupedSections).map(([section_title, fieldsArr]) => ({
      section_title,
      items: fieldsArr.map((fields) => {
        const entry = Object.entries(fields)
          .map(([key, value]) => ({ key, value: value == null ? "" : String(value) }))
          .filter((f) => f.value !== "");
        return entry.length > 0 ? entry : [{ key: "", value: "" }];
      }),
    })),
    social: { instagram_url: "", facebook_url: "", twitter_url: "" },
  };
};

// Reverse of mapDraftToFormValues — what gets PUT back as draft_data on save.
// Merges onto the existing draft rather than replacing it wholesale, so
// fields the review form doesn't manage (e.g. raw LLM metadata) survive.
//
// Deliberately never includes `alternate_email` — it's a top-level sibling
// of draft_data on the PUT body, not a part of draft_data itself.
export const mapFormValuesToDraft = (formData, baseDraft) => {
  const additional_sections = (formData.additionalSections || [])
    .flatMap((section) =>
      (section.items || []).map((entry) => ({
        section_title: section.section_title || "Other",
        fields: Object.fromEntries((entry || []).filter((f) => f?.key?.trim()).map((f) => [f.key.trim(), f.value ?? ""])),
      })),
    )
    .filter((s) => Object.keys(s.fields).length > 0);

  return {
    ...baseDraft,
    personal_information: {
      ...(baseDraft?.personal_information || {}),
      full_name: formData.personal_information?.name || "",
      email: formData.personal_information?.email || "",
      phone: formData.personal_information?.phone || "",
      location: formData.personal_information?.location || "",
      linkedin: formData.personal_information?.linkedin || "",
      website: websiteValue(formData.personal_information?.portfolio),
    },
    summary: formData.personal_information?.summary || "",
    skills: formData.skills?.list || [],
    // Composite strings — matching reads the stored list raw.
    languages: toLanguageStrings(formData.skills?.languages),
    work_experience: (formData.experience || []).map((e) => ({
      company: e.company || "",
      title: e.jobTitle || "",
      start_date: e.startDate || "",
      end_date: e.endDate?.toLowerCase() === "present" ? "Present" : e.endDate || "",
      location: e.location || "",
      description: e.description || "",
      achievements: e.achievements || [],
    })),
    education: (formData.education || []).map((e) => ({
      institution: e.institution || "",
      degree: e.degree || "",
      field_of_study: e.fieldOfStudy || "",
      start_date: e.startDate || "",
      end_date: e.endDate || "",
      grade: e.grade || "",
    })),
    current_salary: formData.compensation?.current_salary || null,
    expected_salary: formData.compensation?.expected_salary || null,
    notice_period: formData.compensation?.notice_period || null,
    total_experience_years: formData.compensation?.total_experience || null,
    additional_sections,
  };
};
